import React, { useEffect, useState } from 'react';
import { signUp } from '@aws-amplify/auth';  // ✅ Gen 2 import

import './RegisterPage.css';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    given_name: '',
    family_name: '',
    email: '',
    birthdate: '',
    phone_number: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    password: '',
    confirmPassword: '',
    plan: 'monthly',
    classDate: '',
    middle_name: ''
  });

  const [availableDates, setAvailableDates] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/availableSlots`, {
      headers: {
        'x-api-key': process.env.REACT_APP_ROBOKIDS_API_KEY
      }
    })
      .then(res => res.json())
      .then(data => setAvailableDates(data.slots || []))
      .catch(err => console.error("❌ Could not fetch available slots:", err));
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.middle_name.trim()) return;

    if (formData.password !== formData.confirmPassword) {
      setMessage("❌ Passwords don't match.");
      return;
    }

    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=<>?{}\[\]~]).{8,}$/;
    if (!regex.test(formData.password)) {
      setMessage("❌ Password must include 1 capital letter, 1 number, 1 special character, and be at least 8 characters.");
      return;
    }

    const formattedPhone = formData.phone_number.replace(/[^\d]/g, '');
    const e164Phone = formattedPhone.length === 10 ? `+1${formattedPhone}` : `+${formattedPhone}`;
    const fullAddress = `${formData.street}, ${formData.city}, ${formData.state} ${formData.zip}`;

    try {
      // ✅ Use Amplify Gen 2 signUp function
      await signUp({
        username: `user_${Date.now()}`,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            given_name: formData.given_name,
            family_name: formData.family_name,
            birthdate: formData.birthdate,
            phone_number: e164Phone,
            address: fullAddress
          }
        }
      });

      // ✅ Save for SuccessPage processing
      sessionStorage.setItem('pendingUser', JSON.stringify({ ...formData, phone_number: e164Phone, address: fullAddress }));

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/createStripeSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_ROBOKIDS_API_KEY
        },
        body: JSON.stringify({
          email: formData.email,
          plan: formData.plan,
          classDate: formData.classDate
        })
      });

      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error(data.message || 'Stripe error');

    } catch (error) {
      console.error(error);
      setMessage(error.message || 'Something went wrong.');
    }
  };

  const todayMinus18Years = new Date(new Date().setFullYear(new Date().getFullYear() - 18) - 86400000).toISOString().split('T')[0];

  return (
    <div className="register-container">
      <h1>Create Your ChronoTech Account</h1>
      {message && <p className="register-message">{message}</p>}
      <form onSubmit={handleSubmit} className="register-form">
        <input type="text" name="middle_name" style={{ display: 'none' }} onChange={handleChange} value={formData.middle_name} />

        <label>First Name:
          <input type="text" name="given_name" value={formData.given_name} onChange={handleChange} required />
        </label>
        <label>Last Name:
          <input type="text" name="family_name" value={formData.family_name} onChange={handleChange} required />
        </label>
        <label>Email:
          <input type="email" name="email" value={formData.email} onChange={handleChange} required />
        </label>
        <label>Password:
          <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} required />
        </label>
        <label>Confirm Password:
          <input type={showPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
        </label>

        <div className="register-show-password">
          <input type="checkbox" id="showPassword" checked={showPassword} onChange={() => setShowPassword(!showPassword)} />
          <label htmlFor="showPassword">Show Password</label>
        </div>

        <ul className="password-rules">
          <li style={{ color: /[A-Z]/.test(formData.password) ? 'green' : 'gray' }}>• One capital letter</li>
          <li style={{ color: /\d/.test(formData.password) ? 'green' : 'gray' }}>• One number</li>
          <li style={{ color: /[!@#$%^&*()_\-+=<>?{}\[\]~]/.test(formData.password) ? 'green' : 'gray' }}>• One special character</li>
          <li style={{ color: formData.password.length >= 8 ? 'green' : 'gray' }}>• At least 8 characters</li>
        </ul>

        <label>Birthdate:
          <input type="date" name="birthdate" value={formData.birthdate} onChange={handleChange} max={todayMinus18Years} required />
        </label>
        <label>Phone Number:
          <input type="tel" name="phone_number" value={formData.phone_number} onChange={handleChange} required />
        </label>
        <label>Street:
          <input type="text" name="street" value={formData.street} onChange={handleChange} required />
        </label>
        <label>City:
          <input type="text" name="city" value={formData.city} onChange={handleChange} required />
        </label>
        <label>State:
          <input type="text" name="state" value={formData.state} onChange={handleChange} required />
        </label>
        <label>ZIP Code:
          <input type="text" name="zip" value={formData.zip} onChange={handleChange} required />
        </label>
        <label>Membership Plan:
          <select name="plan" value={formData.plan} onChange={handleChange}>
            <option value="monthly">$7 / month</option>
            <option value="yearly">$60 / year</option>
          </select>
        </label>

        <label>Choose Class Date:
          <select name="classDate" value={formData.classDate} onChange={handleChange} required>
            <option value="">-- Select a Date --</option>
            {availableDates.map(slot => (
              <option key={slot.classDate} value={slot.classDate}>
                {slot.classDate} — {slot.availableSlots} spots left
              </option>
            ))}
          </select>
        </label>

        <p style={{ fontSize: '0.85rem', color: '#888' }}>
          🔒 You’ll be required to verify your phone number later from your profile page.
        </p>

        <button type="submit" className="submit-btn">Proceed to Payment</button>
      </form>
    </div>
  );
}
